import React, { useState, useEffect } from 'react';
import './Module.css';
import Network from '../../../models/Network.js';
import ReportListElement from '../list_elements/ReportListElement.js';
import InfoCircle from '../../public/demo/InfoCircle.js';

const URLS = require("../../../urls.js");

const DemoReportsListModule = ({ isSmallScreen, onReportSelect, onReportFeedback, sessionAgency }) => {
    const [demoReports, setDemoReports] = useState([]);
    const [agencyReports, setAgencyReports] = useState([]);
    const [agencyUid, setAgencyUid] = useState(null);
    const [loadingAgencyReports, setLoadingAgencyReports] = useState(false);

    const getDemoReports = async () => {
        const url = URLS.GET_DEMO_REPORTS;
        const dict = {
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        };

        await Network.post(url, dict, (response) => {
            if (Network.checkValidStatusAndResponse(response)) {
                setDemoReports(response.data);
            } else {
                console.error('Error fetching demo reports');
            }
        });
    };

    const getAgencyUid = async () => {
        if (!sessionAgency || sessionAgency === 'demo agency') return;
        
        const url = URLS.GET_AGENCY_UID_FROM_DEMO_CODE;
        const dict = {
            demo_code: sessionAgency,
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        };

        await Network.post(url, dict, (response) => {
            if (Network.checkValidStatusAndResponse(response)) {
                setAgencyUid(response.data.uid);
            } else {
                console.error('Error fetching agency UID');
            }
        });
    };

    const getAgencyReports = async () => {
        if (!agencyUid) return;
        
        setLoadingAgencyReports(true);
        const url = URLS.GET_SEVERAL_REPORTS_FOR_AGENCY;
        const dict = {
            agency_uid: agencyUid,
            number_of_reports: 10,
            offset: 0,
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        };

        await Network.post(url, dict, (response) => {
            setLoadingAgencyReports(false);
            if (Network.checkValidStatusAndResponse(response)) {
                setAgencyReports(response.data);
                console.log(response.data);
            } else {
                console.error('Error fetching agency reports');
            }
        });
    };

    const handleReportClick = (reportId) => {
        const url = URLS.GET_ONE_DEMO_REPORT;
        const dict = {
            demo_report_id: reportId,
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        };

        Network.post(url, dict, (response) => {
            if (Network.checkValidStatusAndResponse(response)) {
                // Find the report in demoReports to get the incident number
                const selectedReport = demoReports.find(report => report.demo_report_id === reportId);
                const reportName = selectedReport ? selectedReport.incident_number : `Report ${reportId}`;
                onReportSelect(response.data.report_xml, reportName);
            } else {
                console.error('Error fetching demo report');
            }
        });
    };

    const handleAgencyReportClick = (reportId) => {
        // For agency reports, go directly to feedback view
        const selectedReport = agencyReports.find(report => report.report_id === reportId);
        const reportName = selectedReport ? (selectedReport.incident_number || `Report ${reportId}`) : `Report ${reportId}`;
        onReportFeedback(reportId, reportName);
    };

    useEffect(() => {
        getDemoReports();
    }, []);

    useEffect(() => {
        getAgencyUid();
    }, [sessionAgency]);

    useEffect(() => {
        getAgencyReports();
    }, [agencyUid]);

    return (
        <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
            <div style={{paddingBottom: '15px'}}>
                <h2>Demo Reports</h2>
                <p style={{paddingBottom: '10px'}}>The below PCRs mimic real EMS responses. Choose one to view and have CADMUS evaluate.</p>
                <p>You will have multiple submissions. Previous submissions are shown below.</p>
            </div>
            <div className="list-header">
                <div className='medium-div'>
                    <div style={{fontWeight: "bold"}}>Incident</div>
                </div>
                <div className='tiny-div'>
                    <div style={{fontWeight: "bold"}}></div>
                </div>
                <div className='tiny-div'>
                    <div style={{fontWeight: "bold"}}></div>
                </div>
                <div className='large-div'>
                    <div style={{fontWeight: "bold"}}>
                        Run Type(s)
                        <InfoCircle text="Run Type Tags indicate the nature of the EMS call." white={true} />
                    </div>
                </div>
                <div className='medium-div'>
                    <div style={{fontWeight: "bold"}}></div>
                </div>
                <button className='invisible-enter-button'>View Report</button>
            </div>

            {demoReports.length > 0 ? (
                demoReports
                    .filter(report => report.incident_number !== '2025-demo-04')
                    .map((report) => (
                        <ReportListElement
                            key={report.demo_report_id}
                            reportId={report.demo_report_id}
                            reportNumber={report.demo_report_id}
                            incidentNumber={report.incident_number}
                            numberOfIssues={''}
                            numberOfSuggestions={''}
                            reportType={report.report_types}
                            incidentTime={null}
                            read={false}
                            finished={false}
                            onButtonClick={handleReportClick}
                        />
                    ))
            ) : (
                <p>No demo reports available</p>
            )}

            {/* Agency Reports Section */}
            {sessionAgency && sessionAgency !== 'demo agency' && (
                <div style={{marginTop: '40px'}}>
                    <div style={{paddingBottom: '15px'}}>
                        <h2>Your Submitted Reports</h2>
                        {/* <p style={{paddingBottom: '10px'}}>Reports submitted under demo code: {sessionAgency}</p> */}
                    </div>
                    
                    {loadingAgencyReports ? (
                        <p>Loading your submitted reports...</p>
                    ) : agencyReports.length > 0 ? (
                        <>
                            <div className="list-header">
                                <div className='medium-div'>
                                    <div style={{fontWeight: "bold"}}>Incident</div>
                                </div>
                                <div className='tiny-div'>
                                    <div style={{fontWeight: "bold"}}><i className="fa-solid fa-triangle-exclamation"></i></div>
                                </div>
                                <div className='tiny-div'>
                                    <div style={{fontWeight: "bold"}}><i className="fa-solid fa-message"></i></div>
                                </div>
                                <div className='large-div'>
                                    <div style={{fontWeight: "bold"}}>
                                        Run Type(s)
                                    </div>
                                </div>
                                <div className='medium-div'>
                                    <div style={{fontWeight: "bold"}}>Date Created</div>
                                </div>
                                <button className='invisible-enter-button'>View Report</button>
                            </div>
                            
                            {agencyReports.map((report) => (
                                <ReportListElement
                                    key={report.report_id}
                                    reportId={report.report_id}
                                    reportNumber={report.pcr_number || report.report_id}
                                    incidentNumber={report.incident_number}
                                    numberOfIssues={report.number_issues}
                                    numberOfSuggestions={report.number_suggestions}
                                    reportType={report.report_type}
                                    incidentTime={report.creation_timestamp}
                                    read={report.report_marked_read}
                                    finished={report.report_marked_finished}
                                    onButtonClick={handleAgencyReportClick}
                                />
                            ))}
                        </>
                    ) : (
                        <p>No reports submitted yet. Submit your first report above to see it here.</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default DemoReportsListModule; 